<template>
  <q-card flat class="card-template">
    <q-card-section class="q-py-sm q-card__section q-card__section--vert">
      <div class="crud-title justify-between row items-center">
        <span>{{ $gettext('EXECUTIONS') }}</span>
        <q-chip
          v-if="executions.length > 0"
          color="blue-2"
          text-color="blue"
          dense
        >
          {{ executions.length }}
        </q-chip>
      </div>
    </q-card-section>

    <q-card-section>
      <div v-if="executions.length === 0" class="empty-state text-grey-6">
        <p class="text-body2 text-grey-6">{{ $gettext('NO_EXECUTIONS_FOUND') }}</p>
      </div>

      <div v-else>
        <q-table
          class="executions-table-sticky"
          :rows="executions"
          :data="executions"
          :columns="executionColumns"
          :visible-columns="['id', 'status', 'scheduledDate', 'startedAt', 'finishedAt']"
          :pagination="{ rowsPerPage: 0 }"
          :table-style="{ maxHeight: '320px' }"
          row-key="id"
          flat
          dense
          hide-bottom
        >
          <template v-slot:body-cell-id="props">
            <q-td :props="props">
              <span class="text-caption">{{ (props.pageIndex != null ? props.pageIndex : props.rowIndex) + 1 }}</span>
            </q-td>
          </template>

          <template v-slot:body-cell-status="props">
            <q-td :props="props">
              <div class="row items-center q-gutter-xs">
                <span class="campaign-status-dot" :style="{ backgroundColor: getExecutionStatusColor(props.row) }"></span>
                <span class="text-caption">{{ props.row.campaignExecutionStatus_nmDescricao }}</span>
              </div>
            </q-td>
          </template>

          <template v-slot:body-cell-scheduledDate="props">
            <q-td :props="props">
              <span class="text-caption">{{ formatDate(props.row.scheduledDate) }}</span>
            </q-td>
          </template>

          <template v-slot:body-cell-startedAt="props">
            <q-td :props="props">
              <span class="text-caption">{{ formatDateTime(props.row.startedAt) }}</span>
            </q-td>
          </template>

          <template v-slot:body-cell-finishedAt="props">
            <q-td :props="props">
              <span class="text-caption">{{ formatDateTime(props.row.finishedAt) }}</span>
            </q-td>
          </template>
        </q-table>
      </div>
    </q-card-section>
  </q-card>
</template>

<script>
import { Component, Prop, Vue } from 'vue-property-decorator'

import { campaignExecutionStatus } from 'src/shared/inf-tabela-descricao/CampaignExecutionStatus.enum.js'

@Component({
  name: 'CampaignExecutionsList'
})
class CampaignExecutionsList extends Vue {
  @Prop({ type: Array, default: () => [] }) campaignExecutions

  get executions () {
    const executions = Array.isArray(this.campaignExecutions) ? this.campaignExecutions : []

    return executions
      .filter(execution => String(execution.campaignExecutionStatus_chCodigo) !== '50600005')
      .map(execution => ({
        ...execution,
        scheduledDate: this.normalizeScheduledDate(execution.scheduledDate)
      }))
      .sort((a, b) => this.getScheduledDateSortKey(a.scheduledDate) - this.getScheduledDateSortKey(b.scheduledDate))
  }

  get executionColumns () {
    return [
      { name: 'id', label: '#', field: 'id', align: 'left' },
      { name: 'status', label: this.$gettext('STATUS'), field: 'campaignExecutionStatus_nmDescricao', align: 'left' },
      { name: 'scheduledDate', label: this.$gettext('EXECUTION_DATE'), field: 'scheduledDate', align: 'left' },
      { name: 'startedAt', label: this.$gettext('INIT'), field: 'startedAt', align: 'left' },
      { name: 'finishedAt', label: this.$gettext('END'), field: 'finishedAt', align: 'left' }
    ]
  }

  formatDate (dateStr) {
    if (!dateStr) return ''

    const sqlDateMatch = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T].*)?$/)
    if (sqlDateMatch) {
      const [, year, month, day] = sqlDateMatch
      return `${day}/${month}/${year}`
    }

    const formatted = dateStr
    if (formatted) {
      return formatted.split(',')[0]
    }
    return dateStr
  }

  normalizeScheduledDate (dateStr) {
    if (!dateStr) return dateStr

    const dateValue = String(dateStr)
    const midnightMatch = dateValue.match(/^(\d{4}-\d{2}-\d{2})([ T])00:00:00(\.\d+)?(Z)?$/)
    if (midnightMatch) {
      const [, datePart, separator, milliseconds, zulu] = midnightMatch
      return `${datePart}${separator}22:30:00${milliseconds || ''}${zulu || ''}`
    }

    return dateStr
  }

  getScheduledDateSortKey (dateStr) {
    if (!dateStr) return Number.MAX_SAFE_INTEGER

    const normalizedDate = String(this.normalizeScheduledDate(dateStr))
    const sqlDateTimeMatch = normalizedDate.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/)
    if (sqlDateTimeMatch) {
      const [, year, month, day, hour = '00', minute = '00', second = '00'] = sqlDateTimeMatch
      return Number(`${year}${month}${day}${hour}${minute}${second}`)
    }

    const timestamp = Date.parse(normalizedDate)
    return Number.isNaN(timestamp) ? Number.MAX_SAFE_INTEGER : timestamp
  }

  formatDateTime (dateStr) {
    if (!dateStr) return ''
    const formatted = this.formatDateTimeLocal(dateStr)
    if (formatted) {
      const [date, timePart] = formatted.split(',')
      const [hours, minutes] = timePart.trim().split(':')
      return `${date} ${hours}:${minutes}`
    }
    return dateStr
  }

  formatDateTimeLocal (dateStr) {
    if (!dateStr) return ''

    try {
      // Converte "2026-03-16 20:34:28" para "2026-03-16T20:34:28Z"
      const isoString = dateStr.replace(' ', 'T')
      const dateObj = new Date(isoString)

      if (isNaN(dateObj.getTime())) {
        return dateStr
      }

      // O Date já converte UTC para timezone local ao formatar com toLocaleString
      return dateObj.toLocaleString('pt-BR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    } catch (e) {
      console.error('Erro ao formatar data:', e)
      return dateStr
    }
  }

  getExecutionStatusColor (row) {
    const ces = campaignExecutionStatus()
    const statusCode = row.campaignExecutionStatus_chCodigo

    if (String(statusCode) === String(ces.QUEUED.key)) {
      return 'rgb(14, 136, 228)'
    }
    if (String(statusCode) === String(ces.PROCESSING.key)) {
      return 'rgb(255, 152, 0)'
    }
    if (String(statusCode) === String(ces.COMPLETED.key)) {
      return 'rgb(0, 97, 59)'
    }
    if (String(statusCode) === String(ces.FAILED.key)) {
      return 'rgb(211, 47, 47)'
    }

    return 'rgb(153, 153, 153)'
  }
}

export default CampaignExecutionsList
</script>

<style lang="stylus" scoped>
.card-template
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05)

.empty-state
  display: flex
  flex-direction: column
  align-items: center
  justify-content: center
  padding: 32px 16px
  text-align: center

  p
    margin: 8px 0 0 0

.campaign-status-dot
  width: 12px
  height: 12px
  border-radius: 50%
  display: inline-block

.executions-table-sticky::v-deep .q-table__middle
  max-height: 320px

.executions-table-sticky::v-deep .q-table__middle thead tr th
  position: sticky
  top: 0
  z-index: 2
  background: #ffffff
</style>
